<template>
  <div class="space-y-6">
    <Card padding="none">
      <template #header>
        <div class="px-6 py-4 flex justify-between items-center">
          <h3 class="text-lg font-medium text-text">Team Members</h3>
          <Button size="sm" @click="$emit('invite')">Invite Member</Button>
        </div>
      </template>

      <div class="overflow-x-auto">
        <Table
          :columns="[
            { key: 'member', label: 'Member' },
            { key: 'role', label: 'Role' },
            { key: 'joined', label: 'Joined' },
            { key: 'actions', label: '' },
          ]"
          :rows="members"
        >
          <template #cell-member="{ row: member }">
            <div class="flex items-center">
              <div class="flex-shrink-0 h-10 w-10">
                <OrganizationAvatar :name="member.full_name || member.email" size="md" />
              </div>
              <div class="ml-4">
                <div class="text-sm font-medium text-text">
                  <template v-if="member.user_id === authStore.user?.id">
                    {{ authStore.user?.full_name || authStore.user?.email || 'You' }}
                    <span class="ml-1 text-xs font-normal text-gray-500">(You)</span>
                  </template>
                  <template v-else>
                    {{ member.full_name || member.email || 'Unknown User' }}
                  </template>
                </div>
                <div
                  v-if="
                    (member.full_name ||
                      (member.user_id === authStore.user?.id && authStore.user?.full_name)) &&
                    (member.email ||
                      (member.user_id === authStore.user?.id && authStore.user?.email))
                  "
                  class="text-xs text-gray-500 dark:text-gray-400"
                >
                  {{
                    member.user_id === authStore.user?.id ? authStore.user?.email : member.email
                  }}
                </div>
                <div class="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                  ID: {{ member.user_id.substring(0, 8) }}...
                </div>
              </div>
            </div>
          </template>
          <template #cell-role="{ row: member }">
            <div class="flex items-center">
              <MemberRoleSelect
                v-if="canManageMember(member)"
                :model-value="member.role"
                @change="role => handleRoleChange(member.user_id, role)"
                :is-loading="isLoading"
                class="max-w-[120px]"
              />
              <span
                v-else
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="{
                  'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300':
                    member.role === 'owner',
                  'bg-primary-100 text-primary-800 dark:bg-blue-900/30 dark:text-blue-300':
                    member.role === 'admin',
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300':
                    member.role === 'member',
                }"
              >
                {{ member.role }}
              </span>
            </div>
          </template>
          <template #cell-joined="{ row: member }">
            {{ formatDate(member.joined_at) }}
          </template>
          <template #cell-actions="{ row: member }">
            <div class="text-right">
              <button
                v-if="isOwner && member.user_id !== authStore.user?.id"
                @click="handleTransferOwnership(member)"
                class="text-amber-600 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-300 ml-4"
                title="Transfer Ownership"
              >
                Transfer Ownership
              </button>
              <button
                v-if="canManageMember(member)"
                @click="removeMember(member.user_id)"
                class="text-danger-600 hover:text-danger-900 ml-4"
              >
                Remove
              </button>
            </div>
          </template>
        </Table>
      </div>

      <div v-if="isLoading" class="px-6 py-10 flex justify-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>

      <div
        v-if="!isLoading && members.length === 0"
        class="px-6 py-10 text-center text-gray-500 dark:text-gray-400"
      >
        No team members found.
      </div>
    </Card>
  </div>
</template>

<script setup lang="ts">
  import { onMounted } from 'vue';
  import { useOrganizationMembers, useOrganizationPermissions } from '../composables';
  import type { OrganizationMember } from '../types';
  import { useAuthStore } from '../../../stores/auth';
  import OrganizationAvatar from './OrganizationAvatar.vue';
  import MemberRoleSelect from './MemberRoleSelect.vue';
  import { Card, Button, Table } from '@asabdev/asab-ui';

  defineEmits<{
    (e: 'invite'): void;
  }>();

  const { members, isLoading, fetchMembers, removeMember, updateMemberRole, transferOwnership } =
    useOrganizationMembers();
  const { currentRole, isAdmin, isOwner } = useOrganizationPermissions();
  const authStore = useAuthStore();

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString();
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await updateMemberRole(userId, role as 'admin' | 'member');
    } catch (error) {
      console.error('Failed to update member role:', error);
    }
  };

  const handleTransferOwnership = async (member: OrganizationMember) => {
    const confirmed = confirm(
      `Are you sure you want to transfer ownership to ${
        member.full_name || member.email
      }? You will lose owner permissions and become a regular member.`
    );

    if (confirmed) {
      const password = prompt('Please enter your password to confirm ownership transfer:');
      if (password) {
        try {
          await transferOwnership(member.user_id, password);
        } catch (error) {
          console.error('Failed to transfer ownership:', error);
        }
      }
    }
  };

  const canManageMember = (member: OrganizationMember) => {
    // Cannot remove yourself
    if (member.user_id === authStore.user?.id) return false;

    // Only owners/admins can remove members
    if (!isAdmin.value) return false;

    // Admins cannot remove owners
    if (currentRole.value === 'admin' && member.role === 'owner') return false;

    return true;
  };

  onMounted(() => {
    fetchMembers();
  });
</script>
